import { useTable } from "react-table";
import { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  defaultDateDisplay,
  formatCurrency,
  truncateString,
} from "../../../helpers";
import primaryComponents from "../../primaryComponents";
import { useQuery } from "react-query";
import { getExpenseDetails } from "../../../services/expenses";
import useGetPermission from "../../../hooks/useGetPermission";
import { pathConstants } from "../../../routes/pathContants";
import useExpenseCategories from "../../../pages/appPages/Expenses/ExpenseCategories/useExpenseCategories";
import { ToastContext } from "../../../hooks/context";
import { useUpdateExpenseCategory } from "../../../hooks/api/mutations/expenses";

const useExpensesTable = ({ expenses, isViaSellMode = false }) => {
  const { isFetchingCategories, expenseCategories } = useExpenseCategories();
  const updateExpenseCategory = useUpdateExpenseCategory();
  const isUpdatingCategory = updateExpenseCategory.isLoading;
  const triggerToast = useContext(ToastContext);
  const [isReviewExpense, setIsReviewExpense] = useState(false);
  const [expenseId, setExpenseId] = useState(null);
  const { isPermitted } = useGetPermission({
    permissionParentKey: "Expense",
    permissionChildKey: "can_approve_expense",
  });
  const statusMap = {
    APPROVED: "success--squared",
    DISBURSED: "success--squared",
    PENDING: "pending--squared",
    DECLINED: "failed--text",
  };

  const columns = useMemo(
    () => [
      {
        Header: "Date Created",
        accessor: "expense_date",
        Cell: ({ cell: { value } }) =>
          value ? defaultDateDisplay(value) : "N/A",
      },
      {
        Header: "Expense Name",
        accessor: "name",
        Cell: ({ row, cell: { value } }) => (
          <>
            {!isViaSellMode ? (
              <Link
                className="purple--text smallest-text-size"
                to={pathConstants.EXPENSES_DETAILS({
                  id: row?.original?.id,
                  store_id: row?.original?.store?.id,
                })}
                style={{
                  textDecoration: "underline",
                }}
              >
                {value}
              </Link>
            ) : (
              value
            )}
          </>
        ),
      },
      {
        Header: "Category",
        accessor: "category",
        Cell: ({ cell: { value }, row }) => {
          if (isFetchingCategories) return null;

          return (
            <div style={{ width: "130px" }} className={"selectPropMid"}>
              <primaryComponents.SelectField
                options={expenseCategories}
                value={row?.original?.category?.name || ""}
                placeholder={"Select Category"}
                floatError={false}
                nameKey={"name"}
                idKey={"id"}
                onChange={(selectedCategory) => {
                  const categoryId = selectedCategory?.id || selectedCategory;
                  updateExpenseCategory.mutate(
                    {
                      expenseId: row.original.id,
                      categoryId,
                    },
                    {
                      onSuccess: () =>
                        triggerToast(
                          "Category updated successfully",
                          "success",
                        ),
                    },
                  );
                }}
                isLoading={isUpdatingCategory}
              />
            </div>
          );
        },
      },

      {
        Header: "Amount",
        accessor: "amount",
        Cell: ({ cell }) =>
          formatCurrency({
            value: cell.value,
            currencyCode: cell?.row?.original?.store?.currency_code,
          }),
      },
      {
        Header: "Initiated By",
        accessor: "initiated_by",
        Cell: ({ cell: { value } }) =>
          value ? truncateString(value?.first_name, 15) : "N/A",
      },
      {
        Header: "Approved By",
        accessor: "reviewed_by",
        Cell: ({ cell: { value } }) =>
          value ? truncateString(value?.first_name, 15) : "N/A",
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ cell: { value } }) => (
          <span className={`${statusMap[value]} text-capitalize`}>{value}</span>
        ),
      },
      {
        Header: "Action",
        Cell: ({ row }) => (
          <primaryComponents.Button
            onClick={() => {
              setIsReviewExpense(true);
              setExpenseId(row?.original?.id);
            }}
            classNames={"btn btn--primary smallBtn"}
            isDisabled={row?.original?.status !== "PENDING" || !isPermitted}
          >
            Review
          </primaryComponents.Button>
        ),
      },
    ],
    [expenseCategories],
  );

  const { data: expenseDetails, isLoading: isFetchingExpenseDetails } =
    useQuery(
      ["getExpenseDetails", expenseId],
      () =>
        getExpenseDetails(expenseId).then((res) => {
          return res.data?.data;
        }),
      {
        enabled: expenseId !== null && isReviewExpense,
      },
    );

  const instance = useTable({ columns, data: expenses });
  return {
    instance,
    isReviewExpense,
    isFetchingExpenseDetails,
    expenseDetails: expenseDetails || {},
    setIsReviewExpense,
  };
};

export default useExpensesTable;
